import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { toast } from 'react-toastify';

import { useApiClient } from '@/api/clients/useApiClient.util.ts';
import { GetIndividualCustomerResponseDto } from '@/api/generated';
import { QueryKeyEnum } from '@/api/model/QueryKey.enum.ts';
import { ToastConfig } from '@/config/ToastConfig.util.ts';
import { captureAppException } from '@/monitoring/sentry.util.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';

type Req = {
  customerId: number | undefined;
  disable?: boolean;
};

type Return = {
  customerDetails: GetIndividualCustomerResponseDto | undefined;
  isErrorGettingCustomerDetails: boolean;
  isGettingCustomerDetails: boolean;
};

export const useGetCustomerDetailsIndividual = (req: Req): Return => {
  const { customersApi } = useApiClient();
  const { t } = useTranslationWithPrefix('utils.errorMessages');

  const { data, isError, isPending } = useQuery<
    GetIndividualCustomerResponseDto | undefined,
    AxiosError
  >({
    enabled: !req.disable && Boolean(req.customerId),
    queryFn: async () => {
      if (!req.customerId) {
        captureAppException(new Error('brak customerId'), {
          contextName: 'useGetCustomerDetailsIndividual',
        });
        toast(t('unknownError'), ToastConfig.error);
        return;
      }
      const { data } = await customersApi.getIndividualCustomer(req.customerId);
      return data;
    },
    queryKey: [QueryKeyEnum.CUSTOMERS.DETAILS, req.customerId],
  });

  return {
    customerDetails: data,
    isErrorGettingCustomerDetails: isError,
    isGettingCustomerDetails: isPending,
  };
};
